
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { 
  Layers, 
  Users, 
  Binary, 
  Shield, 
  Code2, 
  LineChart,
  Workflow,
  Briefcase
} from "lucide-react";
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PostMergerPlaybookPage: React.FC = () => {
  const playbookSteps = [
    {
      title: "Technology Stack Optimization",
      icon: <Code2 className="w-6 h-6 text-blue-600" />,
      description: "Modernize infrastructure while preserving core functionality and data integrity.",
      subItems: [
        "Technical debt assessment",
        "Infrastructure optimization",
        "Security protocol enhancement"
      ]
    },
    {
      title: "Team Integration & Retention",
      icon: <Users className="w-6 h-6 text-green-600" />,
      description: "Preserve key talent and foster cultural alignment.",
      subItems: [
        "Leadership retention plans",
        "Skills assessment",
        "Culture integration workshops"
      ]
    },
    {
      title: "Operations Streamlining",
      icon: <Workflow className="w-6 h-6 text-purple-600" />,
      description: "Optimize processes while maintaining business continuity.",
      subItems: [
        "Process mapping",
        "Efficiency optimization",
        "SOP documentation"
      ]
    },
    {
      title: "Data & Systems Integration",
      icon: <Binary className="w-6 h-6 text-orange-600" />,
      description: "Seamless integration of data systems and reporting.",
      subItems: [
        "Data migration strategy",
        "System consolidation",
        "Reporting alignment"
      ]
    },
    {
      title: "Customer Success & Support",
      icon: <Shield className="w-6 h-6 text-red-600" />,
      description: "Maintain and enhance customer relationships throughout transition.",
      subItems: [
        "Communication planning",
        "Service level maintenance",
        "Support team integration"
      ]
    },
    {
      title: "Growth & Optimization",
      icon: <LineChart className="w-6 h-6 text-indigo-600" />,
      description: "Identify and execute on growth opportunities.",
      subItems: [
        "Market expansion planning",
        "Cross-sell opportunities",
        "Product roadmap alignment"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Post-Merger Integration Playbook
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our systematic approach to preserving value while unlocking growth potential in acquired companies
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {playbookSteps.map((step, index) => (
            <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="space-y-1">
                <div className="flex items-center gap-3">
                  {step.icon}
                  <CardTitle className="text-xl">{step.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">{step.description}</p>
                <div className="space-y-2">
                  {step.subItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-gray-50">
                        {item}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PostMergerPlaybookPage;
